import { Box, TextField, Typography } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';

export type DescriptionFieldWithScrollHintProps = {
  descriptionRows: number;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  label: string;
  placeholder?: string;
  scrollForMoreLabel: string;
  size?: 'small' | 'medium';
} & Omit<
  React.ComponentProps<typeof TextField>,
  'multiline' | 'minRows' | 'maxRows' | 'variant' | 'fullWidth' | 'inputRef' | 'ref'
>;

/** Detects overflow via scrollHeight vs clientHeight (reliable on desktop and mobile). */
export const DescriptionFieldWithScrollHint = forwardRef<HTMLTextAreaElement, DescriptionFieldWithScrollHintProps>(
  function DescriptionFieldWithScrollHint(
    { descriptionRows, disabled, error, helperText, label, placeholder, scrollForMoreLabel, size = 'medium', ...field },
    forwardedRef,
  ) {
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const [hasOverflow, setHasOverflow] = useState(false);

    const checkOverflow = useCallback(() => {
      const el = textareaRef.current;
      if (!el) return;
      const overflowing = el.scrollHeight > el.clientHeight;
      setHasOverflow((prev) => (prev !== overflowing ? overflowing : prev));
    }, []);

    useEffect(() => {
      const el = textareaRef.current;
      if (!el) return;
      const raf = requestAnimationFrame(() => {
        checkOverflow();
      });
      const observer = new ResizeObserver(() => {
        requestAnimationFrame(checkOverflow);
      });
      observer.observe(el);
      return () => {
        cancelAnimationFrame(raf);
        observer.disconnect();
      };
    }, [field.value, checkOverflow]);

    const assignRef = useCallback(
      (instance: HTMLTextAreaElement | null) => {
        textareaRef.current = instance;
        if (typeof forwardedRef === 'function') {
          forwardedRef(instance);
        } else if (forwardedRef) {
          (forwardedRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = instance;
        }
      },
      [forwardedRef],
    );

    return (
      <Box>
        <TextField
          {...field}
          inputRef={assignRef}
          fullWidth
          multiline
          minRows={descriptionRows}
          maxRows={descriptionRows}
          variant="standard"
          size={size}
          label={label}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          disabled={disabled}
        />
        {hasOverflow && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              mt: 0.5,
              color: 'text.secondary',
              fontSize: '0.75rem',
            }}
          >
            <KeyboardArrowDown sx={{ fontSize: 16 }} />
            <Typography variant="caption" color="text.secondary">
              {scrollForMoreLabel}
            </Typography>
          </Box>
        )}
      </Box>
    );
  },
);
